var $ = require('jquery');
var _ = require('underscore');
var TorsoService = require('backbone-torso/modules/ServiceCell');
var billingConfigModel = require('./billingConfigModel');
var deprecatedFinancialFeatureConfigModel = require('./financialFeatureConfigModel');
var demographicsCollection = require('./../demographics/demographicsCollection');
var applicationUtils = require('./../applicationUtils');
var userService = require('./../user/userService');

var IS_ON_ADMIN_TOOL = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL;

var ROLES = {
  STAFF_BILL_PAY: 'kiosk.admin.billingInfo.staff.billPay'
};

var PAYMENTS_DISABLED_REASON = {
  BILL_PAY_DISABLED: 'BILL_PAY_DISABLED',
  MISSING_PATIENT_IDENTIFIER: 'MISSING_PATIENT_IDENTIFIER'
};

/**
 * Singleton service for managing the financial feature. Aims to provide convenience utilities to determine the availability of
 * bill pay
 *
 * @class financialFeatureService
 * @static
 * @extends Torso.ServiceCell
 */
var financialFeatureService = new (TorsoService.extend({

  /**
   * @method initialize
   * @override
   */
  initialize: function() {
    _.bindAll(this, '_setAbilityToPay', '_hasPatientIdentifier');
    this._reset();
    this.patientCollection = demographicsCollection.createPrivateCollection();
  },

  /**
   * @method _fetchPatientInformation
   * @param patientVecnaId the vecna Id of the patient
   * @returns {Promise} returns a promise that resolves after patient demographics model has been fetched
   * @private
   */
  _fetchPatientInformation: function(patientVecnaId) {
    var trackedIds = this.patientCollection.getTrackedIds();
    trackedIds = _.union(trackedIds, [patientVecnaId]);
    return this.patientCollection.trackAndPull(trackedIds);
  },

  /**
   * Resets the service state, clears out any saved promise
   * @method _reset
   * @private
   */
  _reset: function() {
    this.abilityToPayInfoMap = {};
  },

  /**
   * @method fetchPayAbility
   * @param patientVecnaId the vecna Id of the patient
   * @returns {Promise} returns a promise that resolves after models have been fetched and the current users
   * ability to pay bills has been determined
   */
  fetchPayAbility: function(patientVecnaId) {
    var abilityToPayInfoMap = this.abilityToPayInfoMap;
    var abilityToPayPromise = abilityToPayInfoMap[patientVecnaId] && abilityToPayInfoMap[patientVecnaId].abilityToPayPromise;

    var thisService = this;
    if (!abilityToPayPromise) {
      abilityToPayInfoMap[patientVecnaId] = {};
      abilityToPayInfoMap[patientVecnaId].loading= true;
      abilityToPayInfoMap[patientVecnaId].abilityToPayPromise = $.when(thisService._fetchPatientInformation(patientVecnaId), billingConfigModel.loadOnce(), deprecatedFinancialFeatureConfigModel.loadOnce())
      .then(function() {
        thisService._setAbilityToPay(patientVecnaId);
      })
      .then(function() {
        abilityToPayInfoMap[patientVecnaId].loading = false;
      });
    }

    return abilityToPayInfoMap[patientVecnaId].abilityToPayPromise;
  },

  /**
   * Set the fields on the service state that represent a users current ability to pay
   * @method _setAbilityToPay
   * @param patientVecnaId the vecna Id of the patient
   * @private
   */
  _setAbilityToPay: function(patientVecnaId) {
    var patientIdentifierSource = billingConfigModel.get('patientIdentifierSourceId');
    var demographicsModel = this.patientCollection.get(patientVecnaId);
    var hasIdentifier = demographicsModel && this._hasPatientIdentifier(patientIdentifierSource, demographicsModel.get('identifiers'));

    var abilityToPayInfoMap = this.abilityToPayInfoMap;

    if (!deprecatedFinancialFeatureConfigModel.isPaymentEnabled()) {
      abilityToPayInfoMap[patientVecnaId].unableToPayReason = PAYMENTS_DISABLED_REASON.BILL_PAY_DISABLED;
      abilityToPayInfoMap[patientVecnaId].ableToPay = false;
    } else if (!patientIdentifierSource || !hasIdentifier) {
      abilityToPayInfoMap[patientVecnaId].unableToPayReason = PAYMENTS_DISABLED_REASON.MISSING_PATIENT_IDENTIFIER;
      abilityToPayInfoMap[patientVecnaId].ableToPay = false;
    } else {
      abilityToPayInfoMap[patientVecnaId].ableToPay = true;
    }
  },

  /**
   * @method _isPaymentEnabled
   * @param isAdminTool whether the currentApplication is admin tool or not
   * @returns {boolean} whether payments are enabled via config
   */
  isPaymentEnabled: function(isAdminTool) {
    if (isAdminTool) {
      return deprecatedFinancialFeatureConfigModel.isPaymentEnabled() && billingConfigModel.get('staffBillpayEnabled');
    } else {
      return deprecatedFinancialFeatureConfigModel.isPaymentEnabled();
    }
  },

  /**
   * @method _hasPatientIdentifier
   * @param patientIdentifierSource source name of identifier to check
   * @param identifiersArray of identifier objects from demographics
   * @returns {boolean} whether patient identifier had a value
   * @private
   */
  _hasPatientIdentifier: function(patientIdentifierSource, identifiersArray) {
    var identifiersMap = _.object(_.map(identifiersArray, function(identifier) {
      return [identifier.sourceName, identifier.identifier];
    }));

    var patientIdentifier = identifiersMap[patientIdentifierSource];
    return !!patientIdentifier;
  },

  /**
   * @method getAbleToPayState
   * @param patientVecnaId the vecna Id of the patient
   * @returns {Object} service state, containing information on the current patient's ability to pay
   *            state.ableToPay - whether a payment can be made
   *            state.unableToPayReason - value representing the reason payment cannot be made
   *            state.loading - whether the service is in a loading state
   *
   */
  getAbleToPayState: function(patientVecnaId) {
    var abilityToPayInfoMap = this.abilityToPayInfoMap;
    return _.omit(abilityToPayInfoMap[patientVecnaId], 'abilityToPayPromise');
  },

  /**
   * Determines if patient payment is allowed.
   * @method patientPaymentAllowed
   * @param patientVecnaId
   * @returns {boolean|*|Object}
   */
  patientPaymentAllowed: function (patientVecnaId) {
    return !IS_ON_ADMIN_TOOL && this.isPaymentEnabled(IS_ON_ADMIN_TOOL) && this.getAbleToPayState(patientVecnaId);
  },

  /**
   * Checks for admin payment role which allows admin bill pay
   * @method checkAdminPaymentPermissions
   * @returns {*|Boolean}
   */
  checkAdminPaymentPermissions: function() {
    return userService.hasRole(ROLES.STAFF_BILL_PAY);
  },

  /**
   * Determines if admin payment is allowed
   * method adminPaymentAllowed
   * @returns {boolean|*|Boolean}
   */
  adminPaymentAllowed: function() {
    return IS_ON_ADMIN_TOOL && this.isPaymentEnabled(IS_ON_ADMIN_TOOL) && this.checkAdminPaymentPermissions();
  }
}))();

financialFeatureService.PAYMENTS_DISABLED_REASON = PAYMENTS_DISABLED_REASON;
financialFeatureService.ROLES = ROLES;
module.exports = financialFeatureService;
